
import { Injectable } from '@angular/core';
import { ApplyForJobDto, HomeDataDto, JobsDto, ProfileDataDto,RawFormApplicationDto,RawFormJobObject,SettingsDataDto, TitleAndLocationDto } from 'src/app/data/dtos';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { Observable, catchError, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { GetDocumentResponseDto } from 'src/app/data/dtos/get-document-response.dto';
import { NotificationsDto } from 'src/app/data/dtos/notifications.dto';
import { AppStateInterface } from 'src/app/store/types';
import * as LanguageActions from '../../store/language/actions';
import * as ColorActions from 'src/app/store/color-theme/actions';
import { Store } from '@ngrx/store';
import { DatePipe } from '@angular/common';
import { selectColorTheme } from 'src/app/store/color-theme/selectors';
import { BrowserService } from '../browser.service';

@Injectable({
  providedIn: 'root',
})
export class DashboardService {
  selectedJob: RawFormJobObject | null = null;
  selectedColortheme: string | null = '';

  constructor(
    private router: Router,
    private _store: Store<AppStateInterface>,
    private _httpClient: HttpClient,
    private _datePipe: DatePipe,
    private readonly browserService: BrowserService,
  ) {
    this._store.dispatch(
      ColorActions.setColorTheme({
        selectedColor: this.browserService.localStorageGetItem('color-theme') ?? 'light',
      }),
    );
    this._store.select(selectColorTheme).subscribe({
      next: result => {
        this.selectedColortheme = result;
      }
    })
  }

  setLanguage(newLanguage: string) {
    this._store.dispatch(
      LanguageActions.setActiveLanguage({ activeLanguage: newLanguage }),
    );
    this.browserService.localStorageSetItem('active-language', newLanguage);
    this.setDirection(newLanguage);
  }

  setDirection(language: string) {
    const browserDocument = this.browserService.document;
    if(browserDocument){
      let rootHasDirection: boolean = browserDocument.documentElement.hasAttribute('dir')
      if(rootHasDirection) {
        this.removeDirectionFromRootElement();
      }
      if (language === 'fa') {
        const elements = browserDocument.querySelectorAll('[dir="ltr"]');
        // input elements must alwas be ltr since we dont allow writing text with rtl directions
        const filteredElements = Array.from(elements).filter(el => {
          return el.tagName.toLowerCase() !== 'input' && el.tagName.toLowerCase() !== 'textarea'}
        );
        filteredElements.forEach((element) => {
          element.setAttribute('dir', 'rtl');
        });
      } else {
        const elements = browserDocument.querySelectorAll('[dir="rtl"]');
        // input elements must alwas be ltr since we dont allow writing text with rtl directions
        const filteredElements = Array.from(elements).filter(el => {
          return el.tagName.toLowerCase() !== 'input' && el.tagName.toLowerCase() !== 'textarea' }
        )
        filteredElements.forEach((element) => {
          element.setAttribute('dir', 'ltr');
        });
      }
    }
  }

  getHomeData(): Observable<HomeDataDto> {
    return this._httpClient
      .post<HomeDataDto>(
        `${environment.api_url}/getDashboardData`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(catchError(this.handleError));
  }

  applyForJob(applyForJobDto: ApplyForJobDto | null) {
    return this._httpClient.post<RawFormApplicationDto>(
      `${environment.api_url}/applyForJob`,
      applyForJobDto,
      {
        withCredentials: true,
      },
    );
  }

  getJobFavorites(): Observable<{jobSlugs: string[]}> {
    return this._httpClient.get<{jobSlugs: string[]}>(`${environment.api_url}/getJobFavorites`, {
      withCredentials:  true
    })
  }

  updateJobFavorites(jobSlugs: string[]) {
    return this._httpClient.post(`${environment.api_url}/updateJobFavorites`, {jobSlugs: jobSlugs},{
      withCredentials:  true
    })
  }

  getNotifications(pagingNumber: number, countPerPage: number): Observable<NotificationsDto>{
    // count per page can be set, but currently is not implemented
    const params = {
      paging: pagingNumber,
      count: countPerPage
    }
    return this._httpClient.get<NotificationsDto>(`${environment.api_url}/notifications`, {
      params:           params,
      withCredentials:  true
    }).pipe(
      catchError(this.handleError))
  }


  getUnseenNotifications(): Observable<NotificationsDto>{
    return this._httpClient.get<NotificationsDto>(`${environment.api_url}/unseenNotifications`, {
      withCredentials:  true
    }).pipe(
      catchError(this.handleError))
  }

  getBookamrkedNotifications(pagingNumber: number): Observable<NotificationsDto>{
    // count per page can be set, but currently is not implemented
    const countPerPage = 25
    const params = {
      paging: pagingNumber,
      conut: countPerPage
    }
    return this._httpClient.get<NotificationsDto>(`${environment.api_url}/bookmarkedNotifications`, {
      params:           params,
      withCredentials:  true
    }).pipe(
      catchError(this.handleError))
  }

  updateNotificationsNewState(notificationIds: string[]) {
    return this._httpClient.post(`${environment.api_url}/updateNotificationsNewState`, {
      ids: notificationIds
    },{
      withCredentials: true
    })
  }

  updateNotificationsMarkedState(notificationIds: string[], state: boolean) {
    return this._httpClient.post(`${environment.api_url}/updateNotificationsMarkedState`, {
      ids: notificationIds,
      state: state
    },{
      withCredentials: true
    })
  }

  deleteNotification(notificationIds: string[]){
    return this._httpClient.post(`${environment.api_url}/deleteNotifications`, {
      ids: notificationIds
    },{
      withCredentials: true
    })
  }

  updateProfile(dto: ProfileDataDto){
    return this._httpClient.post<ProfileDataDto>(`${environment.api_url}/updateProfileData`, dto, {
      withCredentials: true
    }).pipe(
      catchError(this.handleError))
  }

  getProfileData(): Observable<ProfileDataDto> {
    return this._httpClient.get<ProfileDataDto>(
      `${environment.api_url}/getProfileData`,
      {
        withCredentials: true,
      },
    );
  }

  getSettingsData(): Observable<SettingsDataDto> {
    return this._httpClient.get<SettingsDataDto>(
      `${environment.api_url}/getSettingsData`,
      {
        withCredentials: true,
      },
    );
  }

  updateSettingsData(dto: SettingsDataDto) {
    return this._httpClient
      .post(`${environment.api_url}/updateSettingsData`, dto, {
        withCredentials: true,
      })
      .pipe(catchError(this.handleError));
  }

  // ----------- user avatar ----------- //

  getUseravatar(): Observable<GetDocumentResponseDto> {
    return this._httpClient.get<GetDocumentResponseDto>(
      `${environment.api_url}/avatar`,
      {
        withCredentials: true,
      },
    );
  }

  updateUserAvatar(file: FormData): Observable<GetDocumentResponseDto> {
    return this._httpClient.post<GetDocumentResponseDto>(
      `${environment.api_url}/updateAvatar`,
      file,
      {
        withCredentials: true,
      },
    );
  }

  deleteUserAvatar() {
    return this._httpClient.post(
      `${environment.api_url}/deleteAvatar`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  async fetchImageFromS3AndConvertBlobToBase64(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Could not fetch the avatar Blob from pre-signed URL');
    }
    const blob = await response.blob();

    const reader = new FileReader();
    return new Promise<string>((resolve, reject) => {
      reader.onloadend = () => {
        const base64data = reader.result as string;
        resolve(base64data);
      };

      reader.onerror = () => {
        reject(new Error('Failed to read the image as Base64'));
      };

      reader.readAsDataURL(blob);
    });
  }

  // ----------- user application ----------- //

  getUserApplicationDocument(): Observable<GetDocumentResponseDto> {
    return this._httpClient.get<GetDocumentResponseDto>(
      `${environment.api_url}/applicationDocument`,
      { withCredentials: true },
    );
  }

  updateUserApplication(file: FormData): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/updateApplication`,
      file,
      {
        withCredentials: true,
      },
    );
  }

  deleteUserApplication(): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/deleteApplication`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  // ----------- user certification ----------- //

  getUserCertificationDocument(): Observable<GetDocumentResponseDto> {
    return this._httpClient.get<GetDocumentResponseDto>(
      `${environment.api_url}/certificationDocument`,
      { withCredentials: true },
    );
  }

  updateUserCertification(file: FormData): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/updateCertification`,
      file,
      {
        withCredentials: true,
      },
    );
  }

  deleteUserCertification(): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/deleteCertification`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  // ----------- user qualification ----------- //

  getUserQualificationDocument(): Observable<GetDocumentResponseDto> {
    return this._httpClient.get<GetDocumentResponseDto>(
      `${environment.api_url}/qualificationDocument`,
      { withCredentials: true },
    );
  }

  updateUserQualification(file: FormData): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/updateUserQualification`,
      file,
      {
        withCredentials: true,
      },
    );
  }

  deleteUserQualification(): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/deleteQualification`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  // ----------- user Anerkennung ----------- //

  getUserAnerkennungDocument(): Observable<GetDocumentResponseDto> {
    return this._httpClient.get<GetDocumentResponseDto>(
      `${environment.api_url}/anerkennungDocument`,
      { withCredentials: true },
    );
  }

  updateUserAnerkennung(file: FormData): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/updateUserAnerkennung`,
      file,
      {
        withCredentials: true,
      },
    );
  }

  deleteUserAnerkennung(): Observable<boolean> {
    return this._httpClient.post<boolean>(
      `${environment.api_url}/deleteAnerkennung`,
      {},
      {
        withCredentials: true,
      },
    );
  }

  deleteUserAccount() {
    this._httpClient
      .post(
        `${environment.api_url}/removeUserAccount`,
        {},
        {
          withCredentials: true,
        },
      )
      .pipe(catchError(this.handleError))
      .subscribe(() => this.router.navigate(['/']));
  }

  getJobs(query: string): Observable<JobsDto> {
    return this._httpClient.get<JobsDto>(
      `${environment.cms_url}/jobs?${query}`,
      {
        withCredentials: false,
      },
    );
  }

  ConvertToISODateTime(pointSeperatedDateTime: string | null | undefined) {
    if (pointSeperatedDateTime && pointSeperatedDateTime != null) {
      const dateObj = new Date(
        pointSeperatedDateTime.replace(/(\d{2}).(\d{2}).(\d{4})/, '$3-$2-$1'),
      );
      const transformedDate = this._datePipe.transform(
        dateObj,
        'yyyy-MM-ddTHH:mm:ssZ',
      );
      if (transformedDate) {
        return new Date(transformedDate.toString()).toISOString();
      }
      return null;
    } else {
      return null;
    }
  }

  readAsDataURL(file: Blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = (event) => {
        resolve(event.target?.result);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(file);
    });
  }

  generateRandomName(lengthOfString: number): string {
    const characters =
      'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let randomName = '';

    for (let i = 0; i < lengthOfString; i++) {
      const randomIndex = Math.floor(Math.random() * characters.length);
      randomName += characters[randomIndex];
    }
    return randomName;
  }

  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(
        `Backend returned code ${error.status}, body was: `,
        error.error,
      );
    }
    // Return an observable with a user-facing error message.
    return throwError(
      () => new Error('Something bad happened; please try again later.'),
    );
  }

  getLanguageDirection(language: string){
    if(language === 'fa'){
      return 'rtl'
    } else {
      return 'ltr'
    }
  }

  /**
   * this and the subsequent methods are requiered, since pdf-viewer adds the attribute dir="ltr" to the root element to make optimal work of the pdf viewer.
   * since we iterate all elements in the DOM and change the direction if a language with dri=rtl selected we must remove the dir attribute from root element, otherweise the entire layout
   * is messed up
   */
  removeDirectionFromRootElement(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      const rootElement = browserDocument.documentElement;
      if (rootElement.hasAttribute('dir') && rootElement.getAttribute('dir') === 'ltr') {
        rootElement.removeAttribute('dir');
      }
    }
  }

  addDirectionToRootElement(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      const rootElement = browserDocument.documentElement;
      if (!rootElement.hasAttribute('dir')) {
        rootElement.setAttribute('dir', 'ltr');
      }
    }
  }

  overwriteDefaultBackgroundWhiteTheme(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      browserDocument.body.classList.add('bg-gray-200')
    }
  }
  removeOverwrittenBackgroundWhiteTheme(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      browserDocument.body.classList.remove('bg-gray-200')
    }
  }

  deleteJobApplicatio(applicationId: string | null): Observable<boolean>{
    return this._httpClient.post<boolean>(`${environment.api_url}/deleteJobApplication`, {
      applicationId: applicationId
    },{
      withCredentials: true
    })
  }


  getTitleAndLocation(query: string){
    return this._httpClient.get<TitleAndLocationDto>(`${environment.cms_url}/jobs${query}`)
  }

  async convertHeicToSupportedFormat(file: File): Promise<File | null> {
    if (file.type !== 'image/heic' && file.type !== 'image/heif') {
      return file; // Return the original file if it's not .heic
    }

    try {
      const convertedBlob = await this.browserService.convertHeicToAny(file,'image/webp');
      const convertedFile = new File(
        [convertedBlob as Blob],
        file.name.replace(/\.[^/.]+$/, '.webp'), // Change extension to .webp
        { type: 'image/webp' }
      );
      return convertedFile;
    } catch (error) {
      console.error('Error converting HEIC file:', error);
      return null;
    }
  }

  public setTheme(){
    const browserDocument = this.browserService.document;
    if(browserDocument){
      if(this.selectedColortheme === 'light'){
        this._store.dispatch(ColorActions.setColorTheme({ selectedColor: 'dark' }))
        this.browserService.localStorageSetItem('color-theme', 'dark')
        this.removeOverwrittenBackgroundWhiteTheme();
        browserDocument.documentElement.classList.add('dark');
      } else {
        this._store.dispatch(ColorActions.setColorTheme({ selectedColor: 'light' }))
        this.browserService.localStorageSetItem('color-theme', 'light')
        this.overwriteDefaultBackgroundWhiteTheme();
        browserDocument.documentElement.classList.remove('dark');
      }
    }
  }

  getActiveLanguage(): string {
    let cachedSelectedLanguage = this.browserService.localStorageGetItem('active-language');
    if (cachedSelectedLanguage && cachedSelectedLanguage != null) {
      return cachedSelectedLanguage;
    }
    this.browserService.localStorageSetItem('active-language', 'en');
    return 'en';
  }
}
